export const IEM_TILES = "https://mesonet.agron.iastate.edu/cache/tile.py/1.0.0";
const ESRI = "https://server.arcgisonline.com/ArcGIS/rest/services";
export const BASEMAPS = {
  dark: { label: "Dark canvas", base: `${ESRI}/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}`, labels: `${ESRI}/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}`, maxNativeZoom: 16 },
  imagery: { label: "Satellite imagery", base: `${ESRI}/World_Imagery/MapServer/tile/{z}/{y}/{x}`, labels: `${ESRI}/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}`, maxNativeZoom: 17 },
};
export const BASEMAP_URL = BASEMAPS.dark.base;
export const LABELS_URL = BASEMAPS.dark.labels;
export const DEFAULT_VIEW = { lat: 38.6, lon: -96.5, zoom: 4 };
export const VIEW_KEY = "sd-radar-view";
export const PREFS_KEY = "sd-radar-prefs";

const pad = (value) => String(value).padStart(2, "0");

export const PRODUCTS = [
  {
    id: "composite", label: "Reflectivity", short: "REF", unit: "dBZ", frames: 11, site: false, legend: "ref",
    tile: (seq) => `${IEM_TILES}/nexrad-n0q-900913${seq ? `-m${pad(seq * 5)}m` : ""}/{z}/{x}/{y}.png`,
  },
  {
    id: "N0B", label: "Base Reflectivity", short: "BR", unit: "dBZ", frames: 8, site: true, legend: "ref",
    tile: (seq, site) => `${IEM_TILES}/ridge::${site}-N0B-${seq}/{z}/{x}/{y}.png`,
  },
  {
    id: "N0S", label: "Storm Relative Velocity", short: "SRV", unit: "kt", frames: 8, site: true, legend: "vel",
    tile: (seq, site) => `${IEM_TILES}/ridge::${site}-N0S-${seq}/{z}/{x}/{y}.png`,
  },
];

export const SATELLITES = [
  { id: "none", label: "Off" },
  {
    id: "geocolor", label: "GeoColor", maxNativeZoom: 7,
    tile: (hemi) => `https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/GOES-${hemi}_ABI_GeoColor/default/default/GoogleMapsCompatible_Level7/{z}/{y}/{x}.png`,
  },
  { id: "ir", label: "Infrared", maxNativeZoom: 8, tile: (hemi) => `${IEM_TILES}/goes-${hemi.toLowerCase()}-ir-4km-900913/{z}/{x}/{y}.png` },
  { id: "vis", label: "Visible", maxNativeZoom: 9, tile: (hemi) => `${IEM_TILES}/goes-${hemi.toLowerCase()}-vis-1km-900913/{z}/{x}/{y}.png` },
];

export const LEGENDS = {
  ref: [
    { v: 5, c: "#04e9e7" }, { v: 10, c: "#019ff4" }, { v: 15, c: "#0300f4" }, { v: 20, c: "#02fd02" },
    { v: 25, c: "#01c501" }, { v: 30, c: "#008e00" }, { v: 35, c: "#fdf802" }, { v: 40, c: "#e5bc00" },
    { v: 45, c: "#fd9500" }, { v: 50, c: "#fd0000" }, { v: 55, c: "#d40000" }, { v: 60, c: "#bc0000" },
    { v: 65, c: "#f800fd" }, { v: 70, c: "#9854c6" }, { v: 75, c: "#fdfdfd" },
  ],
  vel: [
    { v: -64, c: "#00ff00" }, { v: -48, c: "#00c800" }, { v: -32, c: "#009600" }, { v: -16, c: "#006400" },
    { v: -5, c: "#4b4b4b" }, { v: 5, c: "#640000" }, { v: 16, c: "#960000" }, { v: 32, c: "#c80000" },
    { v: 48, c: "#ff0000" }, { v: 64, c: "#ff00ff" },
  ],
};

export const hemisphereFor = (lon) => (lon < -106 ? "West" : "East");

export const distanceKm = (lat1, lon1, lat2, lon2) => {
  const rad = Math.PI / 180;
  const dLat = (lat2 - lat1) * rad;
  const dLon = (lon2 - lon1) * rad;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.sqrt(a));
};

export const nearestSite = (sites, lat, lon) => {
  let best = null;
  let bestDistance = Infinity;
  for (const site of sites) {
    const distance = distanceKm(lat, lon, site.lat, site.lon);
    if (distance < bestDistance) { best = site; bestDistance = distance; }
  }
  return best;
};

export const loadJSON = (key, fallback) => {
  try { return { ...fallback, ...(JSON.parse(localStorage.getItem(key)) || {}) }; } catch (_) { return fallback; }
};

export const saveJSON = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch (_) {}
};

export const formatScan = (iso) => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return `${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · ${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}Z`;
};

export const REPORT_COLORS = [
  ["tornado", "#ef4444"], ["funnel", "#f87171"], ["hail", "#22d3ee"], ["wind", "#60a5fa"],
  ["flood", "#34d399"], ["rain", "#2dd4bf"], ["snow", "#e0f2fe"], ["lightning", "#facc15"],
];

export const reportColor = (type = "") => {
  const needle = type.toLowerCase();
  return (REPORT_COLORS.find(([key]) => needle.includes(key)) || [null, "#c084fc"])[1];
};
